import React, { useState, useEffect, useRef } from 'react';
import { useField } from 'formik';
import { ChevronDown, X, Check } from 'lucide-react';
import { clsx } from 'clsx';
import { ProgressSpinner } from './ProgressSpinner';

export interface AutocompleteOption {
  id: number | string;
  name: string;
  label?: string;
}

export interface AutocompleteProps {
  name: string;
  label?: string;
  placeholder?: string;
  options: AutocompleteOption[];
  loading?: boolean;
  onSearch?: (query: string) => void;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  allowCustomValue?: boolean;
}

export function Autocomplete({
  name,
  label,
  placeholder = 'Search...',
  options = [],
  loading = false,
  onSearch,

  disabled = false,
  clearable = true,
  className,
  error: externalError,
  helperText,
  required = false,
  allowCustomValue = false,
}: AutocompleteProps) {
  const [field, meta, helpers] = useField(name);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const error = externalError || (meta.touched && meta.error ? meta.error : '');
  const hasError = Boolean(error);

  // Find selected option
  const selectedOption = options.find(
    (option) => String(option.id) === String(field.value) || option.name === field.value
  );

  // Filter options based on search query
  const filteredOptions = React.useMemo(() => {
    const query = searchQuery.trim();
    return query === ''
      ? options
      : options.filter((option) => option.name.toLowerCase().includes(query.toLowerCase()));
  }, [options, searchQuery]);

  // Handle search input
  useEffect(() => {
    if (onSearch && searchQuery.trim()) {
      const timeoutId = setTimeout(() => {
        onSearch(searchQuery);
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, onSearch]);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery(''); // Reset search when clicking outside
        setHighlightedIndex(-1);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        setHighlightedIndex(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          selectOption(filteredOptions[highlightedIndex]);
        } else if (allowCustomValue && searchQuery.trim()) {
          helpers.setValue(searchQuery);
          setIsOpen(false);
          setSearchQuery('');
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchQuery('');
        break;
    }
  };

  const selectOption = (option: AutocompleteOption) => {
    helpers.setValue(option.id);
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  const clearSelection = () => {
    helpers.setValue('');
    setSearchQuery('');
    setIsOpen(true); // Ensure dropdown opens to show all options
    inputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Always ensure dropdown is open when typing/deleting
    if (!isOpen) {
      setIsOpen(true);
    }

    // Reset highlighted index when searching
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setHighlightedIndex(-1);

    // Don't reset searchQuery here! Let the user continue typing from where they left off
    // Only reset if there's a selected option and we want to start fresh search
    if (selectedOption && !searchQuery) {
      setSearchQuery('');
    }
  };

  const displayValue = selectedOption
    ? selectedOption.label || selectedOption.name
    : allowCustomValue
      ? field.value || ''
      : '';

  // Determine what to show in the input field
  const inputValue = isOpen ? searchQuery : displayValue;

  return (
    <div className={clsx('relative', className)} ref={containerRef}>
      {/* Label */}
      {label && (
        <label
          htmlFor={name}
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className="relative">
        <input
          ref={inputRef}
          id={name}
          name={name}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={clsx(
            'block w-full rounded-lg border transition-colors duration-200',
            'px-4 py-2.5 pr-10 text-base',
            'text-gray-900 dark:text-white',
            'placeholder-gray-400 dark:placeholder-gray-500',
            'bg-white dark:bg-dark-800',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            hasError
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500 dark:border-dark-600',
            disabled && 'cursor-not-allowed bg-gray-50 opacity-50 dark:bg-dark-900'
          )}
          autoComplete="off"
        />

        {/* Right Icons */}
        <div className="absolute inset-y-0 right-0 flex items-center">
          {loading && (
            <div className="mr-8">
              <ProgressSpinner size="sm" />
            </div>
          )}

          {clearable && selectedOption && !disabled && (
            <button
              type="button"
              onClick={clearSelection}
              className="mr-1 rounded p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
            className={clsx(
              'mr-2 rounded p-1 focus:outline-none focus:ring-2 focus:ring-primary-500',
              disabled
                ? 'text-gray-300 dark:text-gray-600'
                : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
            )}
          >
            <ChevronDown
              className={clsx('h-4 w-4 transition-transform duration-200', isOpen && 'rotate-180')}
            />
          </button>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
          {filteredOptions.length > 0 ? (
            <ul ref={listRef} className="max-h-60 overflow-auto">
              {filteredOptions.map((option, index) => (
                <li key={option.id}>
                  <button
                    type="button"
                    className={clsx(
                      'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                      index === highlightedIndex
                        ? 'bg-primary-50 text-primary-900 dark:bg-primary-900/50 dark:text-primary-100'
                        : 'text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700',
                      selectedOption?.id === option.id && 'font-medium'
                    )}
                    onClick={() => selectOption(option)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <span>{option.label || option.name}</span>
                    {selectedOption?.id === option.id && (
                      <Check className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
              {loading ? (
                <div className="flex items-center gap-2">
                  <ProgressSpinner size="sm" />
                  Loading...
                </div>
              ) : searchQuery ? (
                allowCustomValue ? (
                  <button
                    type="button"
                    className="w-full text-left hover:text-gray-700 dark:hover:text-gray-300"
                    onClick={() => {
                      helpers.setValue(searchQuery);
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                  >
                    Use "{searchQuery}"
                  </button>
                ) : (
                  `No results found for "${searchQuery}"`
                )
              ) : (
                'No options available'
              )}
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {/* Helper Text */}
      {helperText && !error && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
      )}
    </div>
  );
}
